// Rules for the "Get race-ready" checklist on the confirmation page.
// Progress is saved on the member's race entry: users/{uid}.subscribedRaces.{legId}.raceReady

export function isDeviceConnected(linkedDevice) {
  return Boolean(linkedDevice) && linkedDevice !== "none";
}

// Referrals are counted under the race's name (the same key the registration function writes),
// so referrals from earlier races don't tick this race's step.
export function countRaceReferrals(referralDoc, raceName) {
  const legs = raceName ? referralDoc?.totals?.[raceName] : null;
  if (!legs || typeof legs !== "object") return 0;
  return Object.values(legs).reduce((sum, count) => sum + (Number(count) || 0), 0);
}

export function getRaceReadySteps({ raceReady = {}, linkedDevice, referralCount = 0, hasBib = true } = {}) {
  const steps = [
    { id: "device", done: isDeviceConnected(linkedDevice) || raceReady?.device === "manual" },
    { id: "picture", done: Boolean(raceReady?.pictureAt) },
    hasBib && { id: "bib", done: Boolean(raceReady?.bibAt) },
    { id: "invite", optional: true, done: referralCount > 0 },
    { id: "dashboard", done: Boolean(raceReady?.dashboardAt) },
  ].filter(Boolean);
  return steps.map((step) => ({ optional: false, ...step }));
}

// The optional step (inviting a friend) never blocks being race-ready.
export function getRaceReadyProgress(steps) {
  const required = steps.filter((step) => !step.optional);
  return {
    done: required.filter((step) => step.done).length,
    total: required.length,
    complete: required.every((step) => step.done),
  };
}

// Home strip "Not now": offered only from day 10 of the challenge. The first tap hides the strip for 3 days,
// the second hides it for good (for that race).
const DAY = 24 * 60 * 60 * 1000;
export const SNOOZE_FROM_DAY = 10;
export const SNOOZE_DAYS = 3;

function toMillis(value) {
  if (!value) return null;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

export function canSnoozeHomeStrip(raceInfo, now = Date.now()) {
  const start = toMillis(raceInfo?.raceDateStart);
  return start !== null && now >= start + SNOOZE_FROM_DAY * DAY;
}

export function isHomeStripSnoozed(raceReady, now = Date.now()) {
  const snoozes = Number(raceReady?.homeSnoozes) || 0;
  if (snoozes >= 2) return true;
  const snoozedAt = toMillis(raceReady?.homeSnoozedAt);
  return snoozes === 1 && snoozedAt !== null && now < snoozedAt + SNOOZE_DAYS * DAY;
}
